import React, { useMemo } from 'react';
import { makeStyles, tokens, Body1 } from '@fluentui/react-components';
import { Bot24Regular, Person24Regular } from '@fluentui/react-icons';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import DOMPurify from 'dompurify';
import { ChatMessage, AdaptiveCardData } from '../types';
import { AdaptiveCardRenderer } from './AdaptiveCardRenderer';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalS,
    maxWidth: '80%',
  },
  userContainer: {
    flexDirection: 'row-reverse',
    alignSelf: 'flex-end',
  },
  botContainer: {
    alignSelf: 'flex-start',
  },
  avatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  userAvatar: {
    background: 'linear-gradient(135deg, #0078d4 0%, #106ebe 100%)',
    color: '#ffffff',
  },
  botAvatar: {
    background: '#ffffff',
    padding: '2px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  botAvatarImg: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    objectFit: 'cover',
  },
  bubble: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: '12px',
    maxWidth: '100%',
    wordBreak: 'break-word',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
  },
  userBubble: {
    background: 'linear-gradient(135deg, #0078d4 0%, #106ebe 100%)',
    color: '#ffffff',
    borderBottomRightRadius: '4px',
  },
  botBubble: {
    backgroundColor: '#ffffff',
    color: '#2f2f2f',
    borderBottomLeftRadius: '4px',
    border: '1px solid #e0e0e0',
  },
  cardBubble: {
    padding: 0,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    border: 'none',
  },
  timestamp: {
    fontSize: tokens.fontSizeBase100,
    color: '#a4a4a4',
    marginTop: tokens.spacingVerticalXS,
    textAlign: 'right',
  },
  streamingDots: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    marginLeft: '4px',
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    animationName: {
      '0%, 80%, 100%': {
        transform: 'scale(0.6)',
        opacity: 0.4,
      },
      '40%': {
        transform: 'scale(1)',
        opacity: 1,
      },
    },
    animationDuration: '1.4s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
  },
  dot1: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    animationDelay: '0s',
  },
  dot2: {
    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
    animationDelay: '0.2s',
  },
  dot3: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    animationDelay: '0.4s',
  },
  dot4: {
    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
    animationDelay: '0.6s',
  },
  dot5: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    animationDelay: '0.8s',
  },
  markdown: {
    '& p': {
      margin: 0,
      marginBottom: tokens.spacingVerticalS,
      '&:last-child': {
        marginBottom: 0,
      },
    },
    '& ul, & ol': {
      margin: 0,
      marginBottom: tokens.spacingVerticalS,
      paddingLeft: tokens.spacingHorizontalL,
      '&:last-child': {
        marginBottom: 0,
      },
    },
    '& li': {
      marginBottom: tokens.spacingVerticalXS,
    },
    '& strong': {
      fontWeight: tokens.fontWeightSemibold,
    },
    '& em': {
      fontStyle: 'italic',
    },
    '& code': {
      backgroundColor: tokens.colorNeutralBackground4,
      padding: '2px 4px',
      borderRadius: tokens.borderRadiusSmall,
      fontFamily: 'monospace',
      fontSize: tokens.fontSizeBase200,
    },
    '& pre': {
      backgroundColor: tokens.colorNeutralBackground4,
      padding: tokens.spacingHorizontalS,
      borderRadius: tokens.borderRadiusSmall,
      overflow: 'auto',
      '& code': {
        backgroundColor: 'transparent',
        padding: 0,
      },
    },
    '& a': {
      color: tokens.colorBrandForeground1,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    '& blockquote': {
      margin: 0,
      marginBottom: tokens.spacingVerticalS,
      paddingLeft: tokens.spacingHorizontalM,
      borderLeft: `3px solid ${tokens.colorNeutralStroke1}`,
      color: tokens.colorNeutralForeground2,
      '&:last-child': {
        marginBottom: 0,
      },
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      margin: 0,
      marginBottom: tokens.spacingVerticalS,
      fontWeight: tokens.fontWeightSemibold,
    },
    '& table': {
      borderCollapse: 'collapse',
      marginBottom: tokens.spacingVerticalS,
      width: '100%',
    },
    '& th, & td': {
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      padding: tokens.spacingHorizontalS,
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: tokens.colorNeutralBackground4,
      fontWeight: tokens.fontWeightSemibold,
    },
  },
});

interface MessageBubbleProps {
  message: ChatMessage;
  onCardAction?: (action: string, data: any) => void;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, onCardAction }) => {
  const styles = useStyles();
  const isUser = message.sender === 'user';
  const hasCard = !!message.adaptiveCard;

  // Debug: Log when card message is received
  if (hasCard) {
    console.log('🎴 MessageBubble received card message:', message.id, message.adaptiveCard);
  }

  // Check if content contains HTML tags
  const containsHtml = useMemo(() => {
    const htmlPattern = /<[a-z][\s\S]*>/i;
    return htmlPattern.test(message.text);
  }, [message.text]);

  // Sanitize HTML content to prevent XSS attacks
  const sanitizedContent = useMemo(() => {
    if (containsHtml) {
      return DOMPurify.sanitize(message.text, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'a', 'ul', 'ol', 'li', 
          'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
          'blockquote', 'code', 'pre', 'span', 'div', 'hr', 'result'],
        ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class', 'border', 'cellpadding', 
          'cellspacing', 'width', 'align', 'valign', 'bgcolor'],
      });
    }
    return message.text;
  }, [message.text, containsHtml]);

  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  };

  const handleCardAction = (action: string, data: any) => {
    if (onCardAction) {
      onCardAction(action, data);
    }
  };

  // Render content based on type (HTML, Markdown, or plain text)
  const renderContent = () => {
    if (hasCard && message.adaptiveCard) {
      return (
        <AdaptiveCardRenderer
          cardJson={message.adaptiveCard.cardJson}
          onAction={handleCardAction}
        />
      );
    }
    
    if (isUser) {
      return <Body1>{message.text}</Body1>;
    }

    // For bot messages: render HTML directly if detected, otherwise use ReactMarkdown
    if (containsHtml) {
      return (
        <div 
          className={styles.markdown}
          dangerouslySetInnerHTML={{ __html: sanitizedContent }} 
        />
      );
    }

    // Use ReactMarkdown for markdown content (also supports inline HTML via rehype-raw)
    return (
      <ReactMarkdown rehypePlugins={[rehypeRaw]}>
        {message.text}
      </ReactMarkdown>
    );
  };

  // Render animated streaming dots
  const renderStreamingDots = () => (
    <span className={styles.streamingDots}>
      <span className={`${styles.dot} ${styles.dot1}`} />
      <span className={`${styles.dot} ${styles.dot2}`} />
      <span className={`${styles.dot} ${styles.dot3}`} />
      <span className={`${styles.dot} ${styles.dot4}`} />
      <span className={`${styles.dot} ${styles.dot5}`} />
    </span>
  );

  return (
    <div
      className={`${styles.container} ${
        isUser ? styles.userContainer : styles.botContainer
      }`}
    >
      <div
        className={`${styles.avatar} ${
          isUser ? styles.userAvatar : styles.botAvatar
        }`}
      >
        {isUser ? (
          <Person24Regular />
        ) : (
          <img src="/Copilot-Studio-logo.png" alt="Copilot" className={styles.botAvatarImg} />
        )}
      </div>
      <div>
        <div
          className={`${styles.bubble} ${
            isUser ? styles.userBubble : styles.botBubble
          } ${hasCard ? styles.cardBubble : ''} ${!isUser && !hasCard ? styles.markdown : ''}`}
        >
          {renderContent()}
          {message.isStreaming && renderStreamingDots()}
        </div>
        <div className={styles.timestamp}>{formatTime(message.timestamp)}</div>
      </div>
    </div>
  );
};
